import { useEffect, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Tab "Riwayat Perubahan" untuk halaman detail/edit pegawai.
 * Hanya ditampilkan untuk role admin, kepala_divisi, direksi.
 *
 * Kolom:
 *   - Tanggal Berlaku  (tanggal_berlaku - bisa backdate, YYYY-MM-DD)
 *   - Tanggal Pencatatan (changed_at - saat perubahan disimpan sistem)
 *   - Field, Nilai Lama, Nilai Baru, Diubah Oleh, Alasan
 */
export function PegawaiHistoryTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pegawai/${userId}/history`);
      setRows(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  return (
    <div data-testid="pegawai-history-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History size={16} className="text-teal-700" />
          <div className="font-semibold text-slate-900">Riwayat Perubahan Data</div>
          <span className="text-[11px] text-slate-500">{rows.length} entri</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="gap-1.5"
          data-testid="pegawai-history-refresh"
        >
          <RefreshCw size={13} /> {loading ? "…" : "Muat Ulang"}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">Tgl Berlaku</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">Dicatat</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Field</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nilai Lama</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nilai Baru</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Diubah Oleh</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Alasan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">
                  Belum ada riwayat perubahan.
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`history-row-${r.id}`} className="hover:bg-slate-50/60">
                  <TableCell className="text-xs text-slate-800 font-mono-clock font-semibold whitespace-nowrap">
                    {r.tanggal_berlaku || <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-[11px] text-slate-500 whitespace-nowrap">
                    {fmtDate(r.changed_at)}
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900 text-sm">{r.field_label || r.field_name}</div>
                    <div className="text-[10px] uppercase text-slate-400 tracking-wider">{r.change_type}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">
                    {r.old_label || r.old_value || <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-teal-900">
                    {r.new_label || r.new_value || <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-semibold text-slate-800">{r.actor_name || "—"}</div>
                    <div className="text-[11px] text-slate-500 font-mono-clock">{r.actor_npp || "—"}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 italic max-w-[220px]">
                    {r.reason || <span className="text-slate-300 not-italic">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
